"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslations } from "next-intl";
import { Search, X } from "lucide-react";
import CatalogSearchBar from "@/components/layout/CatalogSearchBar";

interface CatalogSearchModalProps {
  open: boolean;
  onClose: () => void;
}

export default function CatalogSearchModal({ open, onClose }: CatalogSearchModalProps) {
  const t = useTranslations("Nav");
  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  
  // Состояние для управления анимацией Tailwind
  const [isRendered, setIsRendered] = useState(false);

  // Синхронизация состояния открытия и анимации
  useEffect(() => {
    if (open) {
      // Сначала рендерим модалку в DOM
      setIsRendered(true);
      
      // Сохраняем элемент, который был в фокусе
      previousFocusRef.current = document.activeElement as HTMLElement;

      // Блокируем скролл и убираем прыжок контента
      const originalOverflow = document.body.style.overflow;
      const originalPaddingRight = document.body.style.paddingRight;
      const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;

      if (scrollBarWidth > 0) {
        document.body.style.paddingRight = `${scrollBarWidth}px`;
      }
      document.body.style.overflow = "hidden";

      // Переводим фокус на модальное окно
      modalRef.current?.focus();

      return () => {
        document.body.style.overflow = originalOverflow;
        document.body.style.paddingRight = originalPaddingRight;
        previousFocusRef.current?.focus();
      };
    } else {
      setIsRendered(false);
    }
  }, [open]);

  // Закрытие по нажатию на клавишу Escape
  useEffect(() => {
    if (!open) return;

    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };

    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [open, onClose]);

  // Не рендерим ничего на стороне сервера (SSR) или когда модалка закрыта
  if (typeof window === "undefined" || !open) return null;

  const modalLayout = (
    <div 
      className="fixed inset-0 z-[240] flex justify-center overflow-y-auto overflow-x-hidden pt-[min(12vh,4.5rem)] pb-10"
      role="presentation"
    >
      {/* Backdrop (Задний фон с анимацией Tailwind) */}
      <div
        className={`fixed inset-0 bg-black/40 backdrop-blur-[4px] dark:bg-black/60 transition-opacity duration-300 ease-out ${
          isRendered ? "opacity-100" : "opacity-0"
        }`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Диалоговое окно (Контент с анимацией Tailwind) */}
      <div
        ref={modalRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="catalog-search-modal-title"
        className={`relative z-10 w-full max-w-2xl px-3 outline-none sm:max-w-3xl sm:px-6 transition-all duration-300 ease-out ${
          isRendered ? "opacity-100 scale-100 translate-y-0" : "opacity-0 scale-95 -translate-y-4"
        }`}
      >
        {/* overflow-visible — иначе absolute-список подсказок обрезается */}
        <div className="flex flex-col overflow-visible rounded-2xl border border-gray-200/80 bg-white p-4 shadow-2xl dark:border-gray-800 dark:bg-gray-950 sm:p-5 md:p-6">
          
          {/* Header модального окна */}
          <div className="mb-4 flex items-center justify-between gap-3">
            <div className="flex min-w-0 flex-1 items-center gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-red-50 text-red-600 dark:bg-red-950/40 dark:text-red-400">
                <Search size={18} aria-hidden strokeWidth={2} />
              </span>
              <p 
                id="catalog-search-modal-title" 
                className="truncate text-sm font-semibold text-gray-900 dark:text-gray-50 sm:text-[15px]"
              >
                {t("searchModalTitle")}
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-200"
              aria-label={t("closeSearch")}
            >
              <X size={18} aria-hidden strokeWidth={2} />
            </button>
          </div>

          {/* Поле поиска */}
          <div className="relative w-full">
            <CatalogSearchBar autoFocus modalLayer onNavigate={onClose} />
          </div>

        </div>
      </div>
    </div>
  );

  return createPortal(modalLayout, document.body);
}